import React, { useRef, useEffect, useCallback } from 'react';

const InteractiveGrid = () => {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: -1000, y: -1000 });
  const animRef = useRef(null);
  const scrollRef = useRef(0);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    const mouse = mouseRef.current;
    const spacing = 50;
    const radius = 200;

    const mx = mouse.x;
    const my = mouse.y;

    ctx.clearRect(0, 0, w, h);

    // Subtle parallax offset based on scroll
    const offsetY = -(scrollRef.current * 0.15) % spacing;

    for (let x = 0; x <= w; x += spacing) {
      for (let y = offsetY; y <= h; y += spacing) {
        const dx = x - mx;
        const dy = y - my;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const inRange = dist < radius;
        const intensity = inRange ? 1 - dist / radius : 0;

        // Grid lines
        ctx.strokeStyle = inRange
          ? `rgba(245, 158, 11, ${0.03 + intensity * 0.14})`
          : 'rgba(148, 163, 184, 0.03)';
        ctx.lineWidth = 0.5;

        if (x + spacing <= w) {
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(x + spacing, y);
          ctx.stroke();
        }
        if (y + spacing <= h) {
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(x, y + spacing);
          ctx.stroke();
        }

        // Dots at intersections
        const dotSize = inRange ? 1.5 + intensity * 2.5 : 0.8;
        const dotAlpha = inRange ? 0.15 + intensity * 0.85 : 0.06;
        ctx.beginPath();
        ctx.arc(x, y, dotSize, 0, Math.PI * 2);
        ctx.fillStyle = inRange
          ? `rgba(245, 158, 11, ${dotAlpha})`
          : `rgba(148, 163, 184, ${dotAlpha})`;
        ctx.fill();
      }
    }

    animRef.current = requestAnimationFrame(draw);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resize();
    window.addEventListener('resize', resize);

    const handleMouseMove = (e) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };

    const handleScroll = () => {
      scrollRef.current = window.scrollY;
    };

    const handleMouseLeave = () => {
      mouseRef.current = { x: -1000, y: -1000 };
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('mouseleave', handleMouseLeave);

    animRef.current = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mouseleave', handleMouseLeave);
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [draw]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 w-full h-full z-0 bg-slate-950"
      style={{ pointerEvents: 'none' }}
    />
  );
};

export default InteractiveGrid;
